import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { Rome2rioLogo } from "src/svg/Rome2rioLogo";
import styled from "styled-components";
import useCcpaManagement from "src/privacy-regulations/hooks/useCcpaManagement";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { ManageProfileScreen } from "../../auth/components/ManageProfile/ManageProfileScreen/ManageProfileScreen";
import { UserAccounts } from "../../auth/components/UserAccounts/UserAccounts";
import { Navbar } from "../../components/Navbar/Navbar";
import { spacing } from "../../theme";
import { type LayoutType, useLayout } from "../../utils/hooks/useLayout";
import { useTypedLocation } from "../../utils/hooks/useTypedLocation";
import { navigateToNewState } from "../../utils/location/navigateToNewState";
import { DebugScreen } from "../DebugScreen/DebugScreen";
import { PreferenceScreen } from "./PreferenceScreen";
import messages from "./UserPreferences.messages";
import { UserPreferences } from "./UserPreferences/UserPreferences";

export function UserPreferencesScreen() {
  const location = useTypedLocation();
  const navigate = useNavigate();

  const layout = useLayout(handleLayoutChanged);
  const isMobile = layout === "mobile";

  function handleLayoutChanged(
    newLayout: LayoutType,
    previousLayout: LayoutType,
  ) {
    if (newLayout !== "mobile" && previousLayout === "mobile") {
      // If the user is on the user preferences tab and changes their layout to desktop,
      // the user has no way of backing out of the pane. Since the user preferences are
      // in the site navigation on desktop we can push them back to the search screen.
      navigateToNewState(
        navigate,
        { highlightedTab: "search" },
        location,
        true,
      );
    }
  }

  switch (location.state?.preferencesScreen) {
    case "currency":
      return (
        <div className="lg:h-86">
          <PreferenceScreen preference="currency" />
        </div>
      );
    case "language":
      return (
        <div className="lg:h-86">
          <PreferenceScreen preference="language" />
        </div>
      );
    case "distance":
      return isMobile ? (
        <PreferenceScreen preference="distance" />
      ) : (
        <DisplayScreen />
      );
    case "timeFormat":
      return isMobile ? (
        <PreferenceScreen preference="timeFormat" />
      ) : (
        <DisplayScreen />
      );
    case "manageProfile":
      return <ManageProfileScreen />;
    case "debug":
      return <DebugScreen />;
    default:
      return <MainUserPreferencesScreen />;
  }
}

function DisplayScreen() {
  return (
    <div className="lg:h-86">
      <PreferenceScreen preference="distance" />
      <PreferenceScreen preference="timeFormat" />
    </div>
  );
}

function MainUserPreferencesScreen() {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useTypedLocation();
  const layout = useLayout();
  const [ccpaStatus, handleCcpaLinkClick] = useCcpaManagement();

  function openCurrencyScreen() {
    sendAnalyticsInteractionEvent({
      category: "Preferences",
      action: "Click:EditCurrency",
    });
    navigateToNewState(navigate, { preferencesScreen: "currency" }, location);
  }

  function openLanguageScreen() {
    sendAnalyticsInteractionEvent({
      category: "Preferences",
      action: "Click:EditLanguage",
    });
    navigateToNewState(navigate, { preferencesScreen: "language" }, location);
  }

  function openDistanceScreen() {
    sendAnalyticsInteractionEvent({
      category: "Preferences",
      action: "Click:EditDistance",
    });
    navigateToNewState(navigate, { preferencesScreen: "distance" }, location);
  }

  function openTimeFormatScreen() {
    sendAnalyticsInteractionEvent({
      category: "Preferences",
      action: "Click:EditTimeFormatDistance",
    });
    navigateToNewState(navigate, { preferencesScreen: "timeFormat" }, location);
  }

  function openDebugScreen() {
    navigateToNewState(navigate, { preferencesScreen: "debug" }, location);
  }

  function openGDPRConsentScreen() {
    sendAnalyticsInteractionEvent({
      category: "Preferences",
      action: "Click:ManageConsent",
      label: "gdpr",
    });
    window.googlefc?.callbackQueue?.push(window.googlefc.showRevocationMessage);
  }

  function openUsPrivacyConsentScreen() {
    sendAnalyticsInteractionEvent({
      category: "Preferences",
      action: "Click:ManageConsent",
      label: "usPrivacy",
    });
    handleCcpaLinkClick();
  }

  return (
    <>
      {layout === "mobile" && (
        <>
          <Navbar headingText={intl.formatMessage(messages.pageTitleProfile)} />
          <UserAccountsContainer>
            <Rome2rioContainer>
              <Rome2rioLogo title="logo" tint="pink" dotTint="pink" />
            </Rome2rioContainer>
            <UserAccounts />
          </UserAccountsContainer>
        </>
      )}
      <UserPreferences
        onLanguageClick={openLanguageScreen}
        onDistanceClick={openDistanceScreen}
        onCurrencyClick={openCurrencyScreen}
        onTimeFormatClick={openTimeFormatScreen}
        onDebugClick={openDebugScreen}
        onGDPRConsentClick={openGDPRConsentScreen}
        onUsPrivacyConsentClick={openUsPrivacyConsentScreen}
        ccpaStatus={ccpaStatus}
      />
    </>
  );
}

const UserAccountsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  // Spacing
  margin-bottom: ${spacing.md};
  margin: 0 ${spacing.xl};
`;
const Rome2rioContainer = styled.div`
  margin-bottom: ${spacing.xxxl};
  margin-top: ${spacing.xxxl};
  display: flex;
  width: 160px;
`;
